import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/services/api/client';
import { API_ENDPOINTS } from '@/services/api/endpoints';
import { QUERY_KEYS } from '@/services/api/queryKeys';
import { MUTATION_KEYS } from '@/services/api/mutationKeys';
import { ROUTES } from '@/routes/config';
import { useAppDispatch } from '@/store/redux';
import { loginSuccess, logout as logoutAction } from '@/store/redux/slices/authSlice';
import type { User } from '@/types';

// ============================================
// Types (matching PASSWORD_AUTH_GUIDE.md)
// ============================================

interface LoginPayload {
  email: string;
  password: string;
}

interface AuthUser {
  id: string;
  email: string;
  user_type: string;
  sso_provider: string;
  portal_id: string | null;
  first_name: string;
  last_name: string;
  permissions: Record<string, boolean>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  must_change_password?: boolean;
  has_password?: boolean;
}

interface AuthResponse {
  access_token?: string | null;
  refresh_token?: string | null;
  token_type?: string;
  user?: AuthUser | null;
  mfa_required?: boolean;
  mfa_setup_required?: boolean;
  mfa_token?: string | null;
  device_trust_token?: string | null;
}

interface MFAForcedSetupConfirmRawResponse extends AuthResponse {
  recovery_codes: string[];
  device_trust_token?: string | null;
}

export interface MFASetupResponse {
  secret: string;
  qr_code: string;
  otpauth_uri: string;
}

export interface MFAStatusResponse {
  enabled: boolean;
  recovery_codes_remaining: number;
}

export interface MFAVerifyResponse {
  enabled: boolean;
  recovery_codes: string[];
}

export interface MFACodePayload {
  mfa_token: string;
  code: string;
  remember?: boolean;
}

export interface MFADisablePayload {
  password: string;
}

interface RefreshPayload {
  refresh_token: string;
}

export type LoginResult =
  | { status: 'success'; user: User; token: string; refreshToken: string }
  | { status: 'mfa_required'; mfaToken: string }
  | { status: 'mfa_setup_required'; mfaToken: string };

interface PasswordChangePayload {
  current_password: string;
  new_password: string;
}

interface PasswordSetPayload {
  user_id: string;
  password: string;
}

// ============================================
// Helper Functions
// ============================================

const mapAuthUserToUser = (authUser: AuthUser): User => ({
  id: authUser.id,
  email: authUser.email,
  name: authUser.first_name
    ? `${authUser.first_name} ${authUser.last_name || ''}`.trim()
    : authUser.email.split('@')[0],
  mustChangePassword:
    typeof authUser.must_change_password === 'boolean'
      ? authUser.must_change_password
      : undefined,
  hasPassword:
    typeof authUser.has_password === 'boolean'
      ? authUser.has_password
      : undefined,
});

// ============================================
// Queries
// ============================================

/**
 * Get current user profile
 */
export const useMe = (enabled: boolean = true) => {
  return useQuery({
    queryKey: QUERY_KEYS.AUTH.ME,
    queryFn: async () => {
      const response: AxiosResponse<AuthUser> = await apiClient.get(API_ENDPOINTS.AUTH.ME);
      return mapAuthUserToUser(response.data);
    },
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// ============================================
// Mutations
// ============================================

/**
 * Apply a successful auth response: persist tokens, update Redux + cache, and navigate.
 */
const applyAuthSuccess = (
  user: User,
  token: string,
  refreshToken: string,
  dispatch: ReturnType<typeof useAppDispatch>,
  queryClient: ReturnType<typeof useQueryClient>,
  navigate: ReturnType<typeof useNavigate>,
) => {
  localStorage.setItem('refresh_token', refreshToken);
  dispatch(loginSuccess({ user, token }));
  queryClient.setQueryData(QUERY_KEYS.AUTH.ME, user);
  if (user.mustChangePassword) {
    navigate(ROUTES.CHANGE_PASSWORD);
  } else {
    navigate(ROUTES.DASHBOARD_HOME);
  }
};

/**
 * Login mutation for System Admin / Portal Admin
 * Uses POST /api/v1/auth/login/admin
 *
 * Returns either a successful login (with tokens) or an MFA challenge (mfa_required + mfa_token).
 * Caller must check the result's `status` field to handle MFA.
 */
export const useLogin = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [MUTATION_KEYS.LOGIN],
    mutationFn: async (payload: LoginPayload): Promise<LoginResult> => {
      const response: AxiosResponse<AuthResponse> = await apiClient.post(
        API_ENDPOINTS.AUTH.LOGIN_ADMIN,
        payload
      );

      const { access_token, refresh_token, user, mfa_required, mfa_setup_required, mfa_token, device_trust_token } = response.data;

      if (mfa_required) {
        return { status: 'mfa_required', mfaToken: mfa_token || '' };
      }

      if (mfa_setup_required) {
        return { status: 'mfa_setup_required', mfaToken: mfa_token || '' };
      }

      if (!access_token || !refresh_token || !user) {
        throw new Error('Invalid login response');
      }

      if (device_trust_token) {
        localStorage.setItem('mfa_device_token', device_trust_token);
      }

      return {
        status: 'success',
        user: mapAuthUserToUser(user),
        token: access_token,
        refreshToken: refresh_token,
      };
    },
    onSuccess: (result) => {
      if (result.status === 'success') {
        applyAuthSuccess(
          result.user,
          result.token,
          result.refreshToken,
          dispatch,
          queryClient,
          navigate,
        );
      }
      // mfa_required: caller component handles UI transition
    },
  });
};

/**
 * Refresh token mutation
 * Uses POST /api/v1/auth/refresh
 */
export const useRefreshToken = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['refreshToken'],
    mutationFn: async (): Promise<{ user: User; token: string; refreshToken: string }> => {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response: AxiosResponse<AuthResponse> = await apiClient.post(
        API_ENDPOINTS.AUTH.REFRESH,
        { refresh_token: refreshToken } as RefreshPayload
      );

      const { access_token, refresh_token: newRefreshToken, user } = response.data;

      if (!access_token || !newRefreshToken || !user) {
        throw new Error('Invalid refresh response');
      }

      return {
        user: mapAuthUserToUser(user),
        token: access_token,
        refreshToken: newRefreshToken,
      };
    },
    onSuccess: ({ user, token, refreshToken }) => {
      // Store new refresh token (token rotation)
      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
      }

      // Update Redux state with new access token
      dispatch(loginSuccess({ user, token }));

      // Update user in cache
      queryClient.setQueryData(QUERY_KEYS.AUTH.ME, user);
    },
    onError: () => {
      // Refresh failed, clear everything and redirect to login
      localStorage.removeItem('refresh_token');
      dispatch(logoutAction());
      queryClient.clear();
      window.location.href = ROUTES.LOGIN;
    },
  });
};

/**
 * Logout mutation
 */
export const useLogout = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [MUTATION_KEYS.LOGOUT],
    mutationFn: async () => {
      try {
        await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT);
      } catch {
        // Ignore logout errors - we'll clear local state anyway
      }
    },
    onSuccess: () => {
      // Clear refresh token
      localStorage.removeItem('refresh_token');

      // Clear Redux state
      dispatch(logoutAction());

      // Clear all React Query cache
      queryClient.clear();

      // Navigate to login
      navigate(ROUTES.LOGIN);
    },
  });
};

/**
 * Change own password mutation
 * Uses POST /api/v1/auth/password/change
 */
export interface PasswordPolicy {
  min_length: number;
  require_uppercase: boolean;
  require_lowercase: boolean;
  require_number: boolean;
  require_special: boolean;
  history_count: number;
}

export const usePasswordPolicy = () => {
  return useQuery({
    queryKey: ['password-policy'],
    queryFn: async () => {
      const response: AxiosResponse<PasswordPolicy> = await apiClient.get(API_ENDPOINTS.AUTH.PASSWORD_POLICY);
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useForgotPassword = () => {
  return useMutation({
    mutationKey: ['forgotPassword'],
    mutationFn: async (email: string): Promise<{ message: string }> => {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email });
      return response.data;
    },
  });
};

export const useVerifyResetToken = () => {
  return useMutation({
    mutationKey: ['verifyResetToken'],
    mutationFn: async (token: string): Promise<{ valid: boolean; email: string }> => {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.VERIFY_RESET_TOKEN, { token });
      return response.data;
    },
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationKey: ['resetPassword'],
    mutationFn: async (payload: { token: string; new_password: string }): Promise<{ message: string }> => {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, payload);
      return response.data;
    },
  });
};

export const useChangePassword = () => {
  return useMutation({
    mutationKey: ['changePassword'],
    mutationFn: async (payload: PasswordChangePayload): Promise<{ message: string }> => {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.PASSWORD_CHANGE, payload);
      return response.data;
    },
  });
};

/**
 * Set user password mutation (Admin operation)
 * Uses POST /api/v1/auth/password/set
 */
export const useSetPassword = () => {
  return useMutation({
    mutationKey: ['setPassword'],
    mutationFn: async (payload: PasswordSetPayload): Promise<{ message: string }> => {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.PASSWORD_SET, payload);
      return response.data;
    },
  });
};

// ============================================
// MFA Hooks
// ============================================

// MFA setup (post-login, requires JWT auth) — generates secret + QR code (not enabled until /verify)
export const useMfaSetup = () => {
  return useMutation({
    mutationKey: [MUTATION_KEYS.MFA_SETUP],
    mutationFn: async (): Promise<MFASetupResponse> => {
      const response: AxiosResponse<MFASetupResponse> = await apiClient.post(
        API_ENDPOINTS.AUTH.MFA_SETUP,
        {}
      );
      return response.data;
    },
  });
};

// MFA verify (post-login, requires JWT auth) — confirms first TOTP code, enables MFA, returns recovery codes
export const useMfaVerify = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [MUTATION_KEYS.MFA_VERIFY],
    mutationFn: async (code: string): Promise<MFAVerifyResponse> => {
      const response: AxiosResponse<MFAVerifyResponse> = await apiClient.post(
        API_ENDPOINTS.AUTH.MFA_VERIFY,
        { code }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.AUTH.MFA_STATUS });
    },
  });
};

// MFA validate (during login, public) — verifies TOTP/recovery code with mfa_token, returns full auth tokens
export const useMfaValidate = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [MUTATION_KEYS.MFA_VALIDATE],
    mutationFn: async (payload: MFACodePayload): Promise<{ user: User; token: string; refreshToken: string; deviceTrustToken?: string | null }> => {
      const response: AxiosResponse<AuthResponse> = await apiClient.post(
        API_ENDPOINTS.AUTH.MFA_VALIDATE,
        payload
      );

      const { access_token, refresh_token, user, device_trust_token } = response.data;
      if (!access_token || !refresh_token || !user) {
        throw new Error('Invalid MFA validation response');
      }

      return {
        user: mapAuthUserToUser(user),
        token: access_token,
        refreshToken: refresh_token,
        deviceTrustToken: device_trust_token,
      };
    },
    onSuccess: (result) => {
      if (result.deviceTrustToken) {
        localStorage.setItem('mfa_device_token', result.deviceTrustToken);
      }
      applyAuthSuccess(result.user, result.token, result.refreshToken, dispatch, queryClient, navigate);
    },
  });
};

// MFA disable (requires current password)
export const useMfaDisable = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [MUTATION_KEYS.MFA_DISABLE],
    mutationFn: async (payload: MFADisablePayload): Promise<{ message: string }> => {
      const response = await apiClient.post(API_ENDPOINTS.AUTH.MFA_DISABLE, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.AUTH.MFA_STATUS });
    },
  });
};

// MFA recovery codes regeneration (requires current password)
export const useMfaRegenerateRecoveryCodes = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [MUTATION_KEYS.MFA_RECOVERY],
    mutationFn: async (payload: MFADisablePayload): Promise<MFAVerifyResponse> => {
      const response: AxiosResponse<MFAVerifyResponse> = await apiClient.post(
        API_ENDPOINTS.AUTH.MFA_RECOVERY,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.AUTH.MFA_STATUS });
    },
  });
};

// MFA status (current user)
export const useMfaStatus = (enabled: boolean = true) => {
  return useQuery({
    queryKey: QUERY_KEYS.AUTH.MFA_STATUS,
    queryFn: async (): Promise<MFAStatusResponse> => {
      const response: AxiosResponse<MFAStatusResponse> = await apiClient.get(
        API_ENDPOINTS.AUTH.MFA_STATUS
      );
      return response.data;
    },
    enabled,
    staleTime: 30 * 1000,
  });
};

// MFA forced-setup init (during login, public) — exchange mfa_setup_token for QR code
export const useMfaForcedSetupInit = () => {
  return useMutation({
    mutationKey: [MUTATION_KEYS.MFA_FORCED_SETUP_INIT],
    mutationFn: async (mfaToken: string): Promise<MFASetupResponse> => {
      const response: AxiosResponse<MFASetupResponse> = await apiClient.post(
        API_ENDPOINTS.AUTH.MFA_FORCED_SETUP_INIT,
        { mfa_token: mfaToken }
      );
      return response.data;
    },
  });
};

// MFA forced-setup confirm (during login, public) — verify TOTP, enable MFA, return tokens + recovery codes
// Does NOT auto-navigate. Caller shows recovery codes first, then calls useApplyLoginSuccess to complete.
export const useMfaForcedSetupConfirm = () => {
  return useMutation({
    mutationKey: [MUTATION_KEYS.MFA_FORCED_SETUP_CONFIRM],
    mutationFn: async (
      payload: MFACodePayload
    ): Promise<{ user: User; token: string; refreshToken: string; recoveryCodes: string[]; deviceTrustToken?: string | null }> => {
      const response: AxiosResponse<MFAForcedSetupConfirmRawResponse> = await apiClient.post(
        API_ENDPOINTS.AUTH.MFA_FORCED_SETUP_CONFIRM,
        payload
      );
      const { access_token, refresh_token, user, recovery_codes, device_trust_token } = response.data;
      if (!access_token || !refresh_token || !user) {
        throw new Error('Invalid forced-setup response');
      }
      if (device_trust_token) {
        localStorage.setItem('mfa_device_token', device_trust_token);
      }
      return {
        user: mapAuthUserToUser(user),
        token: access_token,
        refreshToken: refresh_token,
        recoveryCodes: recovery_codes || [],
        deviceTrustToken: device_trust_token,
      };
    },
  });
};

// Helper to finalize login after a deferred MFA flow (forced setup → recovery codes seen → continue).
// Returns a function that persists tokens, updates Redux + cache, and navigates to dashboard.
export const useApplyLoginSuccess = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  return (user: User, token: string, refreshToken: string) =>
    applyAuthSuccess(user, token, refreshToken, dispatch, queryClient, navigate);
};

export default {
  useLogin,
  useLogout,
  useMe,
  useRefreshToken,
  useChangePassword,
  useSetPassword,
  useMfaSetup,
  useMfaVerify,
  useMfaValidate,
  useMfaDisable,
  useMfaRegenerateRecoveryCodes,
  useMfaStatus,
  useMfaForcedSetupInit,
  useMfaForcedSetupConfirm,
};
